import {
  IComboBox,
  type IComboBoxOption,
  IComboBoxProps,
  Icon,
  IRenderFunction,
  ISelectableOption,
  Stack,
} from '@fluentui/react';
import {
  ComboBox,
  comboboxLoader,
  comboboxOptionText,
  comboboxWithFooterStyles,
  IconName,
  IH2OTheme,
  Loader,
  LoaderType,
  TooltipHost,
  useTheme,
} from '@h2oai/ui-kit';
import React from 'react';

import { useQueryMyWorkspaces } from '../../../authz/authz.hooks';
import { userName } from '../../../authz/gen/ai/h2o/user/v1/user_pb';
import type { Workspace } from '../../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { useWorkspaces } from '../../../pages/Orchestrator/WorkspaceProvider';
import { useDebouncedCallback, useUser } from '../../../utils/hooks';
import { ComboBoxInfiniteScroll } from '../../ComboBoxInfiniteScroll/ComboBoxInfiniteScroll';
import { membersDropdownWrapperStyles } from '../Navigation.styles';

interface WorkspacesDropdownProps {
  disabled?: boolean;
  onRenderLowerContent?: IRenderFunction<IComboBoxProps>;
  onChange?: (event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string) => void;
  selectedKey?: string;
}

const onRenderOption =
  (theme: IH2OTheme): IRenderFunction<ISelectableOption> =>
  (item) => {
    if (!item) return null;

    if (item.itemType === 2) return <span>{item.text}</span>;

    return (
      <span
        style={{ ...comboboxOptionText, maxWidth: 250, display: 'flex' }}
        title={`${item.text} (${item.data.name})`}
      >
        <span>{item.text}</span>
        <span style={{ color: theme.palette?.gray600, paddingLeft: 4 }}>(</span>
        <span
          style={{
            color: theme.palette?.gray600,
            direction: 'rtl',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            fontWeight: 300,
          }}
        >
          {item.data.name}
        </span>
        <span style={{ color: theme.palette?.gray600 }}>)</span>
      </span>
    );
  };

export const WorkspacesDropdown = (props: WorkspacesDropdownProps) => {
  const user = useUser();
  const { id, isPublic } = user;
  const { activeWorkspace, activateWorkspace } = useWorkspaces();
  const theme = useTheme();
  const comboboxRef = React.useRef<IComboBox>(null);
  const [searchText, setSearchText] = React.useState('');
  // First query: Get the default workspace specifically
  const defaultWorkspaceQuery = useQueryMyWorkspaces(
    {
      filter: `personal_workspace_subject='${userName.compile({ user: id })}'`,
    },
    {
      enabled: !isPublic,
      keepPreviousData: true,
      select: (data) => {
        if (!data?.pages) return { pages: [], pageParams: [] };

        const workspacesFromAllPages = data.pages.flatMap((page) => page.workspaces || []);
        return { pages: workspacesFromAllPages, pageParams: data.pageParams };
      },
    }
  );

  // Second query: Get all other workspaces (excluding default if it appears)
  const otherWorkspacesQuery = useQueryMyWorkspaces(
    {
      filter: !searchText ? undefined : `"${searchText}"`,
    },
    {
      keepPreviousData: true,
      enabled: !!defaultWorkspaceQuery.data?.pages[0]?.name,
      select: (data) => {
        if (!data?.pages) return { pages: [], pageParams: [] };

        const workspacesFromAllPages = data.pages.flatMap((page) => page.workspaces || []);
        const defaultWorkspaceName = defaultWorkspaceQuery.data?.pages?.[0]?.name;

        const filteredWorkspaces = defaultWorkspaceName
          ? workspacesFromAllPages.filter((workspace) => workspace.name !== defaultWorkspaceName)
          : workspacesFromAllPages;
        return { pages: filteredWorkspaces, pageParams: data.pageParams };
      },
    }
  );

  const workspacesOptions = React.useMemo((): IComboBoxOption[] => {
    const defaultWorkspaces = defaultWorkspaceQuery.data?.pages || [];
    const otherWorkspaces = otherWorkspacesQuery.data?.pages || [];

    if (!defaultWorkspaces.length && !otherWorkspaces.length) return [];

    return [
      {
        itemType: 2,
        key: 'workspace-default',
        text: 'Personal Workspaces',
      },
      ...(defaultWorkspaces.length > 0
        ? [
            {
              key: defaultWorkspaces[0].name || '',
              text: defaultWorkspaces[0].displayName || '',
              data: defaultWorkspaces[0],
            },
          ]
        : []),
      {
        itemType: 2,
        key: 'all-workspaces',
        text: 'All Workspaces',
      },
      ...otherWorkspaces.map((workspace) => ({
        key: workspace.name || '',
        text: workspace.displayName || '',
        data: workspace,
      })),
    ];
  }, [defaultWorkspaceQuery.data?.pages, otherWorkspacesQuery.data?.pages]);

  const hasNextPage = otherWorkspacesQuery.hasNextPage;
  const fetchNextPage = otherWorkspacesQuery.fetchNextPage;
  const isError = defaultWorkspaceQuery.isError || otherWorkspacesQuery.isError;

  const handleSearchDebounce = useDebouncedCallback(setSearchText, 500);

  const handleWorkspaceChange = (_: React.FormEvent<IComboBox>, option?: IComboBoxOption | undefined) => {
    if (!option?.data) return;
    activateWorkspace((option.data as Workspace).name || '');
    handleSearchDebounce.cancel();
    setSearchText('');
  };

  const comboboxStyles = comboboxWithFooterStyles(theme);
  const workspacesLoadingError = isError;

  return (
    <Stack styles={membersDropdownWrapperStyles(theme)} style={{ width: '100%' }}>
      <Stack>
        {props.disabled ? (
          <ComboBox
            useComboBoxAsMenuWidth
            disabled
            options={[{ key: 'locked', text: 'Global' }]}
            selectedKey={'locked'}
            styles={comboboxStyles}
          />
        ) : (
          <ComboBoxInfiniteScroll
            autoComplete="on"
            componentRef={comboboxRef}
            calloutProps={{ calloutMaxHeight: 400 }} // limit the height of the dropdown
            disabled={props.disabled || workspacesLoadingError}
            onChange={props.disabled ? undefined : props.onChange ?? handleWorkspaceChange}
            onClick={() => comboboxRef.current?.focus(true)}
            onFetchMoreItems={!hasNextPage ? undefined : fetchNextPage}
            onInputValueChange={handleSearchDebounce}
            options={workspacesOptions}
            optionsFiltered={Boolean(searchText)}
            placeholder="Select Workspace"
            onRenderLowerContent={props.onRenderLowerContent}
            selectedKey={props.selectedKey || activeWorkspace?.name}
            styles={comboboxStyles}
            onRenderOption={onRenderOption(theme)}
            useComboBoxAsMenuWidth={false}
          />
        )}
        {!workspacesOptions.length && <Loader type={LoaderType.progressIndicator} styles={comboboxLoader} />}
      </Stack>
      {props.disabled ||
        (workspacesLoadingError && (
          <TooltipHost
            content={
              workspacesLoadingError
                ? `Unable to load workspaces.`
                : 'Switching workspaces is not supported on this page.'
            }
            id="disabled-workspaces-dropdown"
          >
            <Icon
              iconName={IconName.InfoSolid}
              styles={workspacesLoadingError ? { root: { color: theme.palette?.red500 } } : undefined}
            />
          </TooltipHost>
        ))}
    </Stack>
  );
};
